!function () {
  'use strict';

  const selectors = {
    consentForm: 'form[data-contact-form], form[data-newsletter-form]',
    validationError: '[data-form-validation-error]',
    requiredCheckbox: 'input[type="checkbox"][required]',
    submitButton: 'button[type="submit"], input[type="submit"]',
  };

  const classes = {
    consentErrorVisible: 'is-consent-error-visible',
    checkboxError: 'is-error',
  };

  const boundAttr = 'data-form-validation-bound';
  let clickDelegateBound = false;

  function isConsentForm(form) {
    return form?.matches?.('[data-contact-form], [data-newsletter-form]');
  }

  function getConsentMessage(form) {
    if (form?.matches('[data-newsletter-form]')) {
      return (
        window.theme?.strings?.newsletterConsentRequired ||
        window.theme?.strings?.formConsentRequired ||
        'Please accept the required consent to continue.'
      );
    }

    return (
      window.theme?.strings?.formConsentRequired ||
      'Please accept the required consent to continue.'
    );
  }

  function getValidationErrorElement(form) {
    return form.querySelector(selectors.validationError);
  }

  function clearConsentError(form) {
    const errorElement = getValidationErrorElement(form);

    if (errorElement) {
      errorElement.hidden = true;
      errorElement.setAttribute('aria-hidden', 'true');
      errorElement.classList.remove(classes.consentErrorVisible);
    }

    form.querySelectorAll(`${selectors.requiredCheckbox}.${classes.checkboxError}`).forEach((checkbox) => {
      checkbox.classList.remove(classes.checkboxError);
    });
  }

  function showConsentError(form) {
    const errorElement = getValidationErrorElement(form);
    const message = getConsentMessage(form);

    if (errorElement) {
      const messageElement = errorElement.querySelector('[data-form-validation-error-text]');

      if (messageElement) {
        messageElement.textContent = message;
      } else {
        errorElement.textContent = message;
      }

      errorElement.hidden = false;
      errorElement.removeAttribute('aria-hidden');
      errorElement.classList.add(classes.consentErrorVisible);

      requestAnimationFrame(() => {
        errorElement.scrollIntoView({behavior: 'smooth', block: 'nearest'});
      });
    }

    const uncheckedCheckbox = form.querySelector(`${selectors.requiredCheckbox}:not(:checked)`);

    if (uncheckedCheckbox) {
      uncheckedCheckbox.classList.add(classes.checkboxError);
      uncheckedCheckbox.focus({preventScroll: true});
    }
  }

  function validateRequiredCheckboxes(form) {
    const requiredCheckboxes = form.querySelectorAll(selectors.requiredCheckbox);

    for (const checkbox of requiredCheckboxes) {
      if (!checkbox.checked) {
        return false;
      }
    }

    return true;
  }

  function validateFormBeforeSubmit(form) {
    clearConsentError(form);

    if (!validateRequiredCheckboxes(form)) {
      showConsentError(form);
      return false;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }

    return true;
  }

  function blockEvent(event) {
    if (!event) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }

  function guardFormSubmit(form, event) {
    if (!validateFormBeforeSubmit(form)) {
      blockEvent(event);
      return false;
    }

    return true;
  }

  function onFormSubmit(event) {
    const form = event.target;

    if (!isConsentForm(form)) return;

    guardFormSubmit(form, event);
  }

  function onDocumentClick(event) {
    const button = event.target.closest(selectors.submitButton);

    if (!button) return;

    const form = button.form || button.closest('form');

    if (!isConsentForm(form)) return;

    guardFormSubmit(form, event);
  }

  function bindForm(form) {
    if (form.getAttribute(boundAttr) === 'true') return;

    form.setAttribute(boundAttr, 'true');
    form.addEventListener('submit', onFormSubmit, true);

    form.querySelectorAll(selectors.requiredCheckbox).forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          clearConsentError(form);
        }
      });
    });
  }

  function bindClickDelegate() {
    if (clickDelegateBound) return;

    clickDelegateBound = true;
    document.addEventListener('click', onDocumentClick, true);
  }

  function initForms(root = document) {
    root.querySelectorAll(selectors.consentForm).forEach(bindForm);
    bindClickDelegate();
  }

  window.theme = window.theme || {};
  window.theme.validateFormBeforeSubmit = validateFormBeforeSubmit;
  window.theme.clearFormConsentError = clearConsentError;

  document.addEventListener('shopify:section:load', (event) => {
    initForms(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initForms());
  } else {
    initForms();
  }
}();
